// The CMS same-day BP rule (scripts/bp-session.mjs), the app's only door to it. Gaps vs WF37: app readings carry no
// attrib (a typed reading counts as a cuff one here), pairBpReadings keeps the LAST duplicate where WF37 keeps the
// first, and the zone is patients.timezone (set at registration) where WF37 reads the Withings getmeas zone.
import {
  bpLocalClock, bpSessionMean, bpSessionWindow, bpTimeZone, BP_SESSION_DEFAULT_TZ, BP_SESSION_MIN_READINGS,
  BP_SESSION_WINDOW_DAYS, type BpSessionResult,
} from '../../../scripts/bp-session.mjs';

export type { BpSessionResult, BpSessionReading } from '../../../scripts/bp-session.mjs';
export { BP_SESSION_MIN_READINGS, BP_SESSION_WINDOW_DAYS, bpSessionMean };

export interface EpochBpReading { epoch: number; systolic: number; diastolic: number }

export interface BpSessionOfOptions {
  nowMs: number;
  reportKind?: 'baseline' | 'quarterly' | 'final';
  timeZone?: string | null;
}

export function bpSessionOf(readings: readonly EpochBpReading[] | null | undefined, opts: BpSessionOfOptions): BpSessionResult {
  const rows = (readings || [])
    .filter((r) => r && Number.isFinite(r.epoch) && Number.isFinite(r.systolic) && Number.isFinite(r.diastolic))
    .map((r) => ({
      ms: r.epoch * 1000, sys: r.systolic, dia: r.diastolic, manual: false, at: new Date(r.epoch * 1000).toISOString(),
    }));
  return bpSessionWindow(rows, { nowMs: opts.nowMs, reportKind: opts.reportKind ?? 'quarterly', timeZone: opts.timeZone ?? null });
}

export const bpSessionCountLabel = (found: number): string => `${found} of ${BP_SESSION_MIN_READINGS} readings on one day`;

/** True when Intl accepts the zone as given — an unknown one must never be labelled "patient's time". */
export function isKnownTimeZone(tz: unknown): tz is string {
  return typeof tz === 'string' && tz.trim() !== '' && bpTimeZone(tz) === tz.trim();
}

/** `YYYY-MM-DD` in the zone (invalid → New York), for grouping readings by the patient's day. */
export function localDayKey(timeZone: string | null | undefined): (epochSec: number) => string {
  const clock = bpLocalClock(timeZone ?? null);
  return (epochSec) => clock(epochSec * 1000).day;
}

/** One instant in the zone (invalid → New York); '' for a non-finite instant. */
export function formatInZone(ms: number, timeZone: string | null | undefined, opts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' }): string {
  if (!Number.isFinite(ms)) return '';
  try {
    return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: bpTimeZone(timeZone) }).format(new Date(ms));
  } catch {
    return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: BP_SESSION_DEFAULT_TZ }).format(new Date(ms));
  }
}

/** A `YYYY-MM-DD` calendar day as "Tue, Oct 28" — anchored at noon UTC so no viewer sees the day before. */
export function formatCalendarDay(ymd: unknown): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd ?? ''));
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  }).format(d);
}

export interface FiledBpReadingLike { at?: string | null; local_day?: string | null; local_time?: string | null; manual?: boolean }
export interface FiledBpRowLike { readings?: readonly FiledBpReadingLike[] | null; day?: unknown; time_zone?: unknown }

/** The CMS modal's line above the BP readings. Without WF37's day + a known zone it stays today's wording. */
export function bpReadingsHeader(row: FiledBpRowLike): { zoned: boolean; text: string } {
  const n = Array.isArray(row.readings) ? row.readings.length : 0;
  const day = formatCalendarDay(row.day);
  if (!day || !isKnownTimeZone(row.time_zone)) return { zoned: false, text: 'Sent individually — CMS computes the average itself' };
  if (n < BP_SESSION_MIN_READINGS) {
    return { zoned: true, text: `The best day so far: ${n} reading${n === 1 ? '' : 's'} on ${day} (patient's time)` };
  }
  return { zoned: true, text: `Sent individually — ${n} readings from ${day} (patient's time). CMS computes the average itself` };
}

// WF37 sends a 24-hour HH:MM; the modal reads 12-hour like formatInZone.
function clock12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  return `${((h ?? 0) % 12) || 12}:${String(m ?? 0).padStart(2, '0')} ${(h ?? 0) < 12 ? 'AM' : 'PM'}`;
}

/** One reading's time beside its value: the patient's clock when zoned, else the UTC date as before. */
export function bpReadingWhen(r: FiledBpReadingLike, row: FiledBpRowLike, zoned: boolean): string | null {
  if (!r.at && !r.local_time) return null;
  if (!zoned) return r.at ? String(r.at).slice(0, 10) : null;
  if (r.local_time && /^\d{2}:\d{2}$/.test(String(r.local_time))) return clock12(String(r.local_time));
  return formatInZone(Date.parse(String(r.at)), isKnownTimeZone(row.time_zone) ? row.time_zone : null) || null;
}
